// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from 'react';
import './PrivacyPolicyModal.css';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="privacy-overlay" onClick={handleOverlayClick}>
      <div className="privacy-modal">
        <div className="privacy-header">
          <h2 className="privacy-title">Privacy Policy</h2>
          <button className="privacy-closeButton" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="privacy-content">
          <section className="privacy-section">
            <h3>Information We Collect</h3>
            <p>
              When you participate in an AI interview, we collect your name, work email, and interview responses
              (audio/video recordings and transcripts). This information is necessary to conduct and evaluate your
              interview.
            </p>
          </section>

          <section className="privacy-section">
            <h3>How We Use Your Information</h3>
            <p>Your information is used solely for:</p>
            <ul>
              <li>Verifying your identity before the interview</li>
              <li>Conducting the AI-powered interview session</li>
              <li>Evaluating your responses and generating interview reports</li>
              <li>Sharing results with the hiring team for the position you applied for</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h3>Data Storage and Security</h3>
            <p>
              Your interview recordings and personal data are stored securely using industry-standard encryption. Access
              is restricted to authorized personnel involved in the hiring process. We retain your data only for as long
              as necessary to complete the hiring process and comply with legal requirements.
            </p>
          </section>

          <section className="privacy-section">
            <h3>Your Rights</h3>
            <p>You have the right to:</p>
            <ul>
              <li>Request access to your personal data</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your data after the hiring process concludes</li>
              <li>Withdraw consent at any time (may affect your application)</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h3>Data Sharing</h3>
            <p>
              We do not sell or share your personal information with third parties for marketing purposes. Your
              interview data is shared only with the hiring team and authorized evaluators for the specific role you are
              interviewing for.
            </p>
          </section>

          <section className="privacy-section">
            <h3>Contact Us</h3>
            <p>
              If you have questions about this privacy policy or wish to exercise your rights, please contact the hiring
              team or organization that invited you to this interview.
            </p>
          </section>

          <section className="privacy-section">
            <p className="privacy-lastUpdated">Last updated: July 2026</p>
          </section>
        </div>

        <div className="privacy-footer">
          <button className="privacy-acceptButton" onClick={onClose}>
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
